import React, { useState, useEffect } from 'react';
import { statisticsService } from '@/application/services';
import { DashboardStatistics } from '@/domain/entities';
import { StatCard } from '../components/StatCard';
import { LineChart } from '../components/LineChart';
import { PieChart } from '../components/PieChart';

type ChartPeriod = 'daily' | 'weekly' | 'monthly';

export const StatisticsPage: React.FC = () => {
  const [statistics, setStatistics] = useState<DashboardStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartPeriod, setChartPeriod] = useState<ChartPeriod>('daily');

  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await statisticsService.getDashboard();
      console.log('Statistics data:', data);
      console.log('Daily chart data:', data.charts?.daily);
      setStatistics(data);
    } catch (err) {
      console.error('Failed to load statistics:', err);
      setError('Не удалось загрузить статистику');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatistics();
  }, []);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Загрузка статистики...</p>
        </div>
      </div>
    );
  }

  if (error || !statistics) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <svg className="w-16 h-16 text-red-500 dark:text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Ошибка загрузки</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error || 'Не удалось загрузить статистику'}</p>
          <button
            onClick={loadStatistics}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Подготовка данных для круговых диаграмм
  const categoryColors = ['#4F46E5', '#7C3AED', '#EC4899', '#F59E0B', '#10B981', '#3B82F6'];
  const productsByCategoryData = statistics.products.byCategory.map((cat, index) => ({
    label: cat.categoryName,
    value: cat.count,
    color: categoryColors[index % categoryColors.length],
  }));

  const statusColors: Record<string, string> = {
    completed: '#10B981',
    pending: '#F59E0B',
    processing: '#3B82F6',
    cancelled: '#EF4444',
  };

  const statusLabels: Record<string, string> = {
    completed: 'Завершен',
    pending: 'Ожидает',
    processing: 'В обработке',
    cancelled: 'Отменен',
  };

  const ordersByStatusData = statistics.orders.byStatus.map(status => ({
    label: statusLabels[status.status] || status.status,
    value: status.count,
    color: statusColors[status.status] || '#6B7280',
  }));

  const chartData = statistics.charts[chartPeriod];
  console.log('Current chart period:', chartPeriod);
  console.log('Chart data for period:', chartData);

  const periodLabels: Record<ChartPeriod, string> = {
    daily: 'По дням (последние 30 дней)',
    weekly: 'По неделям (последние 12 недель)',
    monthly: 'По месяцам (последние 12 месяцев)',
  };

  return (
    <div className="h-full overflow-y-auto pb-4">
      <div className="space-y-4">
        {/* Заголовок */}
        <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Статистика</h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Общая статистика по товарам и заказам</p>
            </div>
            <button
              onClick={loadStatistics}
              className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Обновить
            </button>
          </div>
        </div>

        {/* Карточки статистики */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Всего товаров"
            value={statistics.products.totalProducts}
            color="blue"
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            }
          />
          <StatCard
            title="Всего заказов"
            value={statistics.orders.totalOrders}
            color="purple"
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            }
          />
          <StatCard
            title="Общая выручка"
            value={formatCurrency(statistics.orders.totalRevenue)}
            color="green"
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            title="Средний чек"
            value={formatCurrency(statistics.orders.totalOrders > 0 ? statistics.orders.totalRevenue / statistics.orders.totalOrders : 0)}
            color="orange"
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            }
          />
        </div>

        {/* Круговые диаграммы */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <PieChart
            title="Товары по категориям"
            data={productsByCategoryData}
          />
          <PieChart
            title="Заказы по статусам"
            data={ordersByStatusData}
          />
        </div>

        {/* Переключатель периодов */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Период:</span>
            <div className="flex gap-2">
              {(['daily', 'weekly', 'monthly'] as ChartPeriod[]).map(period => (
                <button
                  key={period}
                  onClick={() => setChartPeriod(period)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    chartPeriod === period
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {period === 'daily' && 'По дням'}
                  {period === 'weekly' && 'По неделям'}
                  {period === 'monthly' && 'По месяцам'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Графики заказов */}
        <div className="grid grid-cols-1 gap-4">
          <LineChart
            data={chartData}
            title={`Количество заказов - ${periodLabels[chartPeriod]}`}
            type="count"
            color="#4F46E5"
          />
          <LineChart
            data={chartData}
            title={`Сумма заказов - ${periodLabels[chartPeriod]}`}
            type="total"
            color="#10B981"
          />
        </div>

        {/* Детальная статистика по статусам */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="p-4">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Детальная статистика по заказам</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Статус
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Количество
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Сумма
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Средний чек
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {statistics.orders.byStatus.map((status) => (
                    <tr key={status.status} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: statusColors[status.status] || '#6B7280' }}
                          />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {statusLabels[status.status] || status.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {status.count}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatCurrency(status.total)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatCurrency(status.count > 0 ? status.total / status.count : 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

